/**
 * The PRACTICE DOCUMENTS API — `tasks/practice-documents.md` §3.1, §7 phase 1.
 *
 * A practice document is a BLANK: a PDF the practice already has (`SAQ.pdf`, a COID form, a
 * medical-aid motivation), uploaded once and attached to a workflow later. This service covers the
 * blank store and nothing else. `PatientDocumentRequest` (§3.3) — the ask, the return, the review —
 * is Phase 2 and belongs to the patient file; a service that reached both would make the settings
 * tab a dependency of the patient file, which is the mistake `custom-form.service.ts` names in its
 * own header and avoids for the same reason.
 *
 * ── WHAT THIS FILE IS CAREFUL ABOUT ──────────────────────────────────────────────────────────
 *
 * **`fillMode` is NOT in `PracticeDocumentMetadata`, deliberately.** §3.1 stores it rather than
 * deriving it per request precisely so a practice *could* force `download` on a document we could
 * embed — but embedded filling is Phase 5 (§7). Until that ships, a control that could set
 * `embedded` would configure a delivery that cannot happen, and the row would then tell the
 * practice their patients fill it in on screen when nothing can. The upload inspection sets it; the
 * settings screen reads it. When Phase 5 lands, this is the one field to add here.
 *
 * **No `Content-Type` is set on the two multipart calls.** `authHeaders()` carries the token and
 * nothing else, so the browser writes `multipart/form-data` with its own boundary. Setting that
 * header by hand is the standard way to break a multipart upload — the boundary goes missing and
 * the server parses no file at all.
 *
 * **Everything the bytes can refuse is refused before the upload.** `hasPdfHeader` /
 * `readPageCount` live here rather than in the component so the *page* that uploads and the *panel*
 * that replaces a file cannot drift apart on what a PDF is. The server re-inspects regardless
 * (§3.1: the A/B/C decision is the server's, once, at upload) — these checks only spend a message
 * instead of an upload, and are never allowed to accept something the server would refuse.
 *
 * ── TWO THINGS THE CONTRACT DID NOT NAME, AND HOW THEY WERE SETTLED ─────────────────────────
 *
 * The Phase 1 contract this was written against named the seven routes but neither the response
 * envelope nor the multipart field names. Both were chosen from the newest controller in the tree
 * and then reconciled against `controllers/practiceDocumentController.js` as it landed.
 *
 * 1. **The envelope is `{ success, data }`**, with `total` on lists and
 *    `{ success: false, error, message, details }` on a refusal — which is what these DTOs declare.
 *    `unwrapList` / `unwrapOne` still accept a BARE array or object: not to paper over a mismatch,
 *    but because the cost of being wrong is a settings tab that renders "no documents yet" against
 *    a working endpoint, and the cost of tolerating it is four lines.
 *
 * 2. **The file travels as `file` and the metadata as `data`** — §1.4's consent idiom. The
 *    controller's `pickUploadedFile` prefers `file`/`document`/`pdf`/`blank` and falls back to the
 *    first upload, and its `readBody` parses a `data` JSON string or flat fields interchangeably,
 *    so either idiom works. `data` is what is sent, because that is the pattern this feature was
 *    told to follow.
 */

import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { GroupService } from './group.service';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * DTOs — mirroring the `PracticeDocuments` row of §3.1
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * §1.1's A/B/C decision, collapsed to the two outcomes v1 ships.
 *
 * Tier B (a flat PDF with a real text layer — the practice's own `SAQ.pdf`) stores `download` until
 * AcroForm synthesis lands, so there is no third value here and there should not be one: a mode the
 * patient page cannot honour is worse than a download.
 */
export type PracticeDocumentFillMode = 'embedded' | 'download';

export interface PracticeDocumentDto {
  id: string;
  groupId: string;
  name: string;
  description: string | null;
  category: string | null;

  /** The blank itself. `mediaUrl` may be a SAS-signed URL that has already expired — see `getFileUrl`. */
  mediaUrl?: string | null;
  mediaFileName: string;
  mediaType: string;

  /**
   * NULLABLE on purpose (§9 decision 4): derived metadata, and "could not tell" must not render as
   * zero. A `pageCount` of `null` means the inspection could not count, not that the PDF is empty.
   */
  fileSizeBytes: number | null;
  pageCount: number | null;

  /**
   * The §1.1 inspection, done once at upload.
   *
   * **Neither of these is a user-facing concept.** They exist so a later phase can upgrade a tier B
   * document, and the settings screen uses them for exactly one thing: explaining *why* a document
   * is download-only. Rendering them as booleans would put "Has text layer: true" in front of a
   * practice manager, which answers no question they have.
   */
  hasAcroForm: boolean;
  hasTextLayer: boolean;

  fillMode: PracticeDocumentFillMode;

  sendWithInvite: boolean;
  /** §0 decision 4 — a HARD BLOCK on finishing the invite. See the settings screen's copy. */
  requiredInInvite: boolean;
  allowManualSend: boolean;

  /**
   * §14.2 — THE FLAG THAT DECIDES EVERYTHING DOWNSTREAM. Stored NOT NULL, default **true**.
   *
   * `true`: the patient gets a token link, a request row that expects a return, an
   * `awaiting_review` on the way back, and a dedupe that refuses a second outstanding ask.
   * `false`: information only — the file is ATTACHED to the email, there is no link, nothing comes
   * back, nothing is reviewed, and the same sheet may be sent as often as the practice likes.
   *
   * Required rather than optional on purpose. A `requiresResponse?: boolean` would let a screen
   * read `undefined` as falsy and quietly describe a document the practice expects back as an
   * information sheet — the silent direction of the failure §14.2 chose the default to avoid.
   */
  requiresResponse: boolean;

  /** §14.6 decision 2 — pre-ticks the picker on a consent request. Configuration, never a force. */
  sendWithConsentRequest: boolean;

  expiryDays: number;

  active: boolean;

  /**
   * §26 — who may sign off a return of this document, and who may validate it onto the timeline.
   *
   * `null` means ANYONE in the practice, which is the default. These hold `Users.id`; see
   * `PracticeDocumentMetadata` for why that distinction is the load-bearing one.
   */
  signOffUserIds?: string[] | null;
  validationUserIds?: string[] | null;

  createdByUserId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

/**
 * The EDITABLE surface, and nothing else.
 *
 * `groupId`, `id`, `createdByUserId` and every inspected field (`mediaUrl`, `mediaFileName`,
 * `mediaType`, `fileSizeBytes`, `pageCount`, `hasAcroForm`, `hasTextLayer`, `fillMode`) are absent
 * by construction. `PUT /:id` is metadata and delivery flags — NOT the file — so a body that cannot
 * express the file's facts is a body that cannot quietly contradict the server's own inspection.
 */
export interface PracticeDocumentMetadata {
  name?: string;
  description?: string | null;
  category?: string | null;
  sendWithInvite?: boolean;
  requiredInInvite?: boolean;
  allowManualSend?: boolean;
  /**
   * §14.2. Set at CREATE, by which of the tab's two sections the add icon was clicked in.
   *
   * The settings dialog no longer sends it: an information sheet is information-only by
   * definition, so a control offering to make one into a document that is asked back offered to
   * make it something it is not. It stays optional on this interface because every field here
   * is, and because the controller applies only the flags a body actually carries — which is
   * what lets an edit omit it and leave the stored value alone.
   */
  requiresResponse?: boolean;
  /** §14.6 decision 2. */
  sendWithConsentRequest?: boolean;
  expiryDays?: number | null;
  active?: boolean;

  /**
   * §26 — WHO MAY ACT ON A RETURN OF THIS DOCUMENT. Two optional lists of `Users.id`.
   *
   * `null` (or absent) means ANYONE in the practice may do that step, which is the default and
   * what every document has until somebody configures one. An empty array is normalised to null
   * server-side, so "configured to nobody" is not a state that can be stored — it would be read
   * as a list permitting no one, which is the lock-out the rule exists to avoid.
   *
   * These hold `Users.id`, NOT `GroupUsers.id`. The group-users endpoint returns rows carrying
   * both; binding the wrong one produces a list that can never match anybody, which the server's
   * empty-intersection fallback then reads as "anyone may act" — a permission that looks
   * configured and enforces nothing, with no error anywhere.
   */
  signOffUserIds?: string[] | null;
  validationUserIds?: string[] | null;
}

/** What a create sends alongside the file. `name` is the only one a practice must give. */
export interface PracticeDocumentCreateMetadata extends PracticeDocumentMetadata {
  name: string;
  groupId?: string;
}

export interface PracticeDocumentListResponse {
  success: boolean;
  data: PracticeDocumentDto[];
  total?: number;
}

export interface PracticeDocumentResponse {
  success: boolean;
  data: PracticeDocumentDto;
}

export interface PracticeDocumentDeleteResponse {
  success: boolean;
  message?: string;
  data?: { id: string };
}

/**
 * `GET /:id/file` — a short-lived URL for the blank, so the practice can check what they uploaded.
 *
 * The URL is signed and expires; nothing caches it. The endpoint also returns `expiresInMinutes`,
 * `mediaFileName`, `mediaType`, `fillMode` and `pageCount` — none of which this client needs, since
 * it already holds the row. `expiresAt` stays optional so a response without one is not a crash.
 */
export interface PracticeDocumentFileUrlResponse {
  success: boolean;
  data: { url: string; expiresAt?: string | null };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * §3.2 / §24.2 — the appointment-type trigger
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * WHEN a booking causes this document to go out. The sign lives here, not in `offsetDays`.
 *
 * `confirmation` is not "0 days before": it is sent inside the booking flow with the confirmation
 * email (§4.1), while `before` with `offsetDays: 0` is the scheduler firing on the day of the
 * appointment. Collapsing them would make a document sent at 08:00 on the day indistinguishable
 * from one sent the moment the receptionist saved the booking.
 */
export type PracticeDocumentSendOn = 'confirmation' | 'before' | 'after';

/**
 * One row of `AppointmentTypePracticeDocuments` (§3.2).
 *
 * **`offsetDays` is DAYS.** §3.2 is explicit: weeks are STORED as days, "3 weeks" is 21, and a UI
 * that offers weeks converts on the way in. No field on this DTO carries a unit, deliberately —
 * two units on one wire is how an off-by-seven arrives.
 *
 * `active: false` is PAUSED, not deleted. The unique quadruple is still occupied by a paused row,
 * which is why the POST is an upsert that finds inactive rows (§24.2) and why unlink is a separate
 * hard DELETE.
 */
export interface PracticeDocumentLinkDto {
  id: string;
  appointmentTypeId: string;
  /** The server resolves the name so the tab does not have to join two lists to draw one chip. */
  appointmentTypeName?: string | null;
  sendOn: PracticeDocumentSendOn;
  offsetDays: number;
  active: boolean;
  practiceDocumentId?: string;
}

/** What a link POST carries. `active` omitted means "the server's default", which is active. */
export interface PracticeDocumentLinkRequest {
  appointmentTypeId: string;
  sendOn: PracticeDocumentSendOn;
  /** DAYS. Always `0` for `confirmation` — the service enforces that rather than trusting a form. */
  offsetDays: number;
  active?: boolean;
}

export interface PracticeDocumentLinkListResponse {
  success: boolean;
  data: PracticeDocumentLinkDto[];
}

/**
 * The upsert's answer — and the three booleans are the ONLY way to read it.
 *
 * The endpoint answers **200 whether it created a row or updated one**, so the status code carries
 * no information here. `created` / `reactivated` / `paused` do, and the distinction is not
 * cosmetic: re-adding a trigger the practice had paused RESUMES the existing row rather than
 * making a second one (the inactive-row trap of §24.2), and a screen that reported that as "added"
 * would send someone looking for a duplicate that does not exist.
 */
export interface PracticeDocumentLinkResponse {
  success: boolean;
  data: PracticeDocumentLinkDto;
  created?: boolean;
  reactivated?: boolean;
  paused?: boolean;
  message?: string;
}

/** The refusal shape every controller in this tree uses. */
export interface PracticeDocumentApiError {
  success: false;
  error: string;
  message: string;
  details?: {
    /**
     * A refusal that names its own ceiling. `inspectOrRefuse` sends both on every 413/422 —
     * see `PracticeDocumentPdfChoice.applyServerCap`.
     */
    maxBytes?: number;
    maxPages?: number;
    /** Tolerated alias for `maxBytes`; nothing on the server sends this today. */
    maxFileBytes?: number;
    openRequestCount?: number;
    [key: string]: any;
  };
}

@Injectable({ providedIn: 'root' })
export class PracticeDocumentService {

  private apiUrl = 'api';

  /**
   * The practice a create lands in.
   *
   * Sent explicitly for the reason `custom-form.service.ts` records: a `groupId` in the body is
   * honoured only where the caller is a member of it, so it can narrow but never widen, and a user
   * in several practices who names none gets a guess instead of the practice their settings page is
   * actually showing.
   */
  selectedGroupId: string | null = null;

  constructor(private http: HttpClient, private groupService: GroupService) {
    // Root-provided and alive as long as the app is, so no takeUntil — the pattern
    // `custom-form.service.ts` and `appointment-type.service.ts` both use.
    this.groupService.selectedGroup$.subscribe((group: any) => {
      this.selectedGroupId = group && group.id ? group.id : null;
    });
  }

  /* ── Reading ────────────────────────────────────────────────────────────────────────────── */

  listDocuments(filters: { category?: string; active?: boolean; groupId?: string } = {})
    : Observable<PracticeDocumentListResponse> {
    let params = new HttpParams();
    if (filters.category) params = params.set('category', filters.category);
    if (filters.active !== undefined) params = params.set('active', String(filters.active));

    // §32 — ALWAYS sent. The server refuses a read that names no practice, and an empty value
    // produces that refusal rather than a silently merged list.
    params = params.set('groupId', this.readGroup(filters.groupId));

    return this.http.get<any>(`${this.apiUrl}/practice-documents`, {
      headers: this.authHeaders(),
      params,
    }).pipe(map((response) => PracticeDocumentService.unwrapList(response)));
  }

  /**
   * THE ATTACHMENT PICKER'S LIST — every ACTIVE document in ONE practice (§14.3).
   *
   * A named method rather than `listDocuments({ active: true })` at four call sites, because
   * "which documents may ride along with something" is one question with one answer, and the
   * picker is shared by hosts in two feature modules. `active: false` is not a filter the picker
   * ever wants: an inactive document is one the practice has switched off, and offering it to be
   * attached would send a patient a blank the practice believes is retired.
   *
   * `groupId` is the PATIENT'S practice where the host knows it. Passing it matters for the same
   * reason the send dialog already passes it — a user who belongs to several practices must be
   * offered the documents of the one this patient is in, not a merged list. Omit it and the
   * service falls back to the practice the user has selected.
   */
  listAttachableDocuments(groupId?: string | null): Observable<PracticeDocumentListResponse> {
    return this.listDocuments(groupId ? { active: true, groupId } : { active: true });
  }

  /**
   * THE PRACTICE EVERY READ IS ABOUT — `tasks/practice-documents.md` §32.
   *
   * The server now REQUIRES `groupId` on every document read and answers `400 GROUP_ID_REQUIRED`
   * without it. It used to scope reads to every practice the caller belongs to, which merged two
   * practices' templates into one list.
   *
   * `groupId` here is the practice the CALLER means — the patient's, where the caller knows it —
   * falling back to the one the user has selected. Returning '' rather than throwing keeps the
   * failure on the server, where it is one clear refusal rather than an exception thrown from a
   * getter halfway through a component's init.
   */
  private readGroup(groupId?: string | null): string {
    const resolved = groupId || this.selectedGroupId;
    return resolved ? String(resolved) : '';
  }

  getDocument(id: string, groupId?: string | null): Observable<PracticeDocumentResponse> {
    return this.http.get<any>(`${this.apiUrl}/practice-documents/${id}`, {
      headers: this.authHeaders(),
      params: new HttpParams().set('groupId', this.readGroup(groupId)),
    }).pipe(map((response) => PracticeDocumentService.unwrapOne(response)));
  }

  /**
   * The short-lived URL for the blank. Never stored — it is fetched at the moment it is opened,
   * because a SAS token held on a settings page that has been open all afternoon has expired.
   */
  getFileUrl(id: string, groupId?: string | null): Observable<PracticeDocumentFileUrlResponse> {
    return this.http.get<any>(`${this.apiUrl}/practice-documents/${id}/file`, {
      headers: this.authHeaders(),
      params: new HttpParams().set('groupId', this.readGroup(groupId)),
    }).pipe(map((response) => {
      const data = response && response.data ? response.data : response;
      const url = data && typeof data === 'object' ? (data.url || data.mediaUrl) : data;
      return { success: true, data: { url, expiresAt: (data && data.expiresAt) || null } };
    }));
  }

  /* ── Writing ────────────────────────────────────────────────────────────────────────────── */

  /**
   * UPLOAD — `POST /api/practice-documents`, multipart, 201.
   *
   * The response carries the SERVER'S inspection: `pageCount`, `hasAcroForm`, `hasTextLayer` and
   * the `fillMode` they decide. The client's own pre-checks (`hasPdfHeader`, `readPageCount`) never
   * write any of those; they only decide whether the upload is worth attempting.
   */
  createDocument(file: File, metadata: PracticeDocumentCreateMetadata)
    : Observable<PracticeDocumentResponse> {
    const body = new FormData();
    body.append('file', file, file.name);

    const payload: any = this.metadataBody(metadata);
    payload.name = metadata.name;
    const groupId = metadata.groupId !== undefined ? metadata.groupId : this.selectedGroupId;
    if (groupId) payload.groupId = groupId;

    // The consent-settings idiom (`tasks/practice-documents.md` §1.4): the metadata travels as one
    // JSON blob under `data`, beside the file.
    body.append('data', JSON.stringify(payload));

    return this.http.post<any>(`${this.apiUrl}/practice-documents`, body, {
      headers: this.authHeaders(),
    }).pipe(map((response) => PracticeDocumentService.unwrapOne(response)));
  }

  /**
   * METADATA AND DELIVERY FLAGS. Never the file.
   *
   * Field by field, never a spread: a spread of the settings form's value would carry `fillMode`,
   * `pageCount` and the two inspection booleans, and a PUT that appeared to change how patients
   * fill a document in — while the stored bytes say otherwise — is the exact lie this feature
   * cannot afford. `undefined` is omitted so a PUT touches only what was edited; `null` is kept,
   */
  updateDocument(id: string, metadata: PracticeDocumentMetadata)
    : Observable<PracticeDocumentResponse> {
    return this.http.put<any>(`${this.apiUrl}/practice-documents/${id}`,
      this.metadataBody(metadata), { headers: this.authHeaders() })
      .pipe(map((response) => PracticeDocumentService.unwrapOne(response)));
  }

  /**
   * REPLACE THE FILE — `POST /api/practice-documents/:id/file`.
   *
   * A separate endpoint from the PUT, and separate here too, because the server RE-INSPECTS what it
   * receives: swapping a scan for a real AcroForm moves the document from tier C to tier A and the
   * `fillMode` on the response is the new answer. The caller must therefore refresh the row from
   * the response rather than assume the row it already holds still describes the file.
   */
  replaceFile(id: string, file: File): Observable<PracticeDocumentResponse> {
    const body = new FormData();
    body.append('file', file, file.name);

    return this.http.post<any>(`${this.apiUrl}/practice-documents/${id}/file`, body, {
      headers: this.authHeaders(),
    }).pipe(map((response) => PracticeDocumentService.unwrapOne(response)));
  }

  /** SOFT delete (§3.1 is paranoid). Completed copies in patient records still resolve the blank. */
  deleteDocument(id: string): Observable<PracticeDocumentDeleteResponse> {
    return this.http.delete<PracticeDocumentDeleteResponse>(
      `${this.apiUrl}/practice-documents/${id}`, { headers: this.authHeaders() });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Appointment-type triggers — §24.2, Phase 4
   *
   * Three calls and no fourth. The booking screen's read
   * (`GET /appointment-types/:id/practice-documents`) is deliberately NOT here: it belongs to the
   * screen that books an appointment, and a settings service that offered it would make the
   * booking screen a consumer of the settings tab's service — the same coupling this file's header
   * refuses for `PatientDocumentRequest`.
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /** Every link of one document, ACTIVE AND PAUSED — the settings tab has to show both. */
  listAppointmentTypeLinks(
    id: string, groupId?: string | null,
  ): Observable<PracticeDocumentLinkListResponse> {
    return this.http.get<any>(`${this.apiUrl}/practice-documents/${id}/appointment-types`, {
      headers: this.authHeaders(),
      params: new HttpParams().set('groupId', this.readGroup(groupId)),
    }).pipe(map((response) => PracticeDocumentService.unwrapLinkList(response)));
  }

  /**
   * LINK, PAUSE and RESUME are the SAME CALL — §24.2 makes the POST an upsert on
   * `(appointmentTypeId, practiceDocumentId, sendOn, offsetDays)` that finds inactive rows too.
   *
   * So `active` is not an optional extra here, it is the whole of pause/resume: a paused link
   * still occupies the unique slot, and posting the same quadruple with `active: true` is what
   * brings it back rather than creating a second row. The trap is recorded in `patient-forms.md`
   * §3.4 and it is the reason DELETE exists separately — see below.
   *
   * **`offsetDays` is DAYS and only days** (§3.2). A UI that offers weeks converts before it gets
   * here; nothing on this boundary knows what a week is, because two units on one wire is how an
   * off-by-seven arrives.
   */
  linkAppointmentType(id: string, link: PracticeDocumentLinkRequest)
    : Observable<PracticeDocumentLinkResponse> {
    const body: any = {
      appointmentTypeId: link.appointmentTypeId,
      sendOn: link.sendOn,
      // §24.2: `0` when `sendOn = 'confirmation'`, `>= 0` otherwise — the sign lives in `sendOn`.
      offsetDays: link.sendOn === 'confirmation' ? 0 : link.offsetDays,
    };
    if (link.active !== undefined) body.active = link.active;

    return this.http.post<any>(`${this.apiUrl}/practice-documents/${id}/appointment-types`,
      body, { headers: this.authHeaders() })
      .pipe(map((response) => PracticeDocumentService.unwrapLinkOne(response)));
  }

  /**
   * UNLINK — a HARD delete (§24.2), and keyed on the LINK ID rather than the appointment type.
   *
   * The unique key includes `sendOn` and `offsetDays`, so one appointment type legitimately holds
   * several links of the same document — at confirmation AND three days before. A delete keyed on
   * the appointment type alone could not say which of them was meant, which is exactly the
   * widening `custom-form.service.ts` had to close by making `sendOn` mandatory at its own
   * boundary. Here the id says it, once.
   */
  unlinkAppointmentType(id: string, linkId: string): Observable<PracticeDocumentDeleteResponse> {
    return this.http.delete<PracticeDocumentDeleteResponse>(
      `${this.apiUrl}/practice-documents/${id}/appointment-types/${linkId}`,
      { headers: this.authHeaders() });
  }

  /** Same tolerance as `unwrapList`, and for the same reason — see the header, gap 1. */
  static unwrapLinkList(response: any): PracticeDocumentLinkListResponse {
    if (Array.isArray(response)) return { success: true, data: response };
    return {
      success: response && response.success !== undefined ? !!response.success : true,
      data: response && Array.isArray(response.data) ? response.data : [],
    };
  }

  /**
   * The outcome booleans are carried through, not dropped.
   *
   * They sit BESIDE `data`, not inside it, and an unwrapper that returned only the row would throw
   * away the single thing that says whether the practice just created a trigger or resumed one
   * they had paused — a fact no later request can recover, because both end states look identical.
   */
  static unwrapLinkOne(response: any): PracticeDocumentLinkResponse {
    const data = response && response.data !== undefined ? response.data : response;
    return {
      success: response && response.success !== undefined ? !!response.success : true,
      data: data as PracticeDocumentLinkDto,
      created: response ? !!response.created : false,
      reactivated: response ? !!response.reactivated : false,
      paused: response ? !!response.paused : false,
      message: response && typeof response.message === 'string' ? response.message : undefined,
    };
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * What the bytes can be asked before an upload
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /** The extension is a CLAIM. It is checked first only so the cheap refusal comes first. */
  static looksLikePdfByName(file: File): boolean {
    const name = (file && file.name ? file.name : '').toLowerCase();
    const type = (file && file.type ? file.type : '').toLowerCase();
    return name.endsWith('.pdf') || type === 'application/pdf';
  }

  /**
   * `%PDF-`, the first five bytes of every PDF. The header is EVIDENCE.
   *
   * A `.docx` renamed to `.pdf` passes `looksLikePdfByName` and fails here, which is the only reason
   * this check exists: the server inspects the bytes with `pdf-lib` and would refuse the same file
   * after paying for the upload.
   */
  static hasPdfHeader(bytes: Uint8Array): boolean {
    if (!bytes || bytes.length < 5) return false;
    return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46
      && bytes[4] === 0x2d;
  }

  /**
   * BEST-EFFORT page count, and best-effort is the point.
   *
   * A page object is `/Type /Page`; the page-tree root carries `/Count n`. Neither is visible when
   * the document uses cross-reference streams and puts its page tree in a compressed object stream —
   * so this returns `null` rather than `0` when it finds nothing, and `null` NEVER refuses an
   * upload. Undercounting costs a server refusal the practice would have had anyway; overcounting
   * would refuse a perfectly good document, so the count that decides is the one that can only be
   * too LOW: explicit page objects, with `/Count` used only when there are none visible at all.
   */
  static readPageCount(bytes: Uint8Array): number | null {
    if (!bytes || bytes.length === 0) return null;

    let text = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      text += String.fromCharCode.apply(
        null, Array.prototype.slice.call(bytes.subarray(i, i + chunk)) as any);
    }

    // `/Type /Page` but NOT `/Type /Pages` — the tree node is not a page.
    const pageObjects = text.match(/\/Type\s*\/Page[^s]/g);
    if (pageObjects && pageObjects.length > 0) return pageObjects.length;

    const count = text.match(/\/Count\s+(\d+)/);
    if (count) {
      const parsed = parseInt(count[1], 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    return null;
  }

  /* ── Helpers ────────────────────────────────────────────────────────────────────────────── */

  /**
   * The server's refusal, or null for anything that is not one (a network drop, a proxy 502, a body
   * that is not JSON). Callers show `message` and branch on `error`.
   */
  static asApiError(error: any): PracticeDocumentApiError | null {
    const body = error instanceof HttpErrorResponse ? error.error : (error && error.error);
    if (body && typeof body === 'object' && typeof body.error === 'string') {
      return body as PracticeDocumentApiError;
    }
    return null;
  }

  /** See the header, gap 1: the envelope is assumed, and a bare array is accepted rather than lost. */
  static unwrapList(response: any): PracticeDocumentListResponse {
    if (Array.isArray(response)) {
      return { success: true, data: response, total: response.length };
    }
    const data = response && Array.isArray(response.data) ? response.data : [];
    return {
      success: response && response.success !== undefined ? !!response.success : true,
      data,
      total: response && typeof response.total === 'number' ? response.total : data.length,
    };
  }

  static unwrapOne(response: any): PracticeDocumentResponse {
    const data = response && response.data !== undefined ? response.data : response;
    return {
      success: response && response.success !== undefined ? !!response.success : true,
      data: data as PracticeDocumentDto,
    };
  }

  private metadataBody(metadata: PracticeDocumentMetadata): any {
    const body: any = {};
    const keys: (keyof PracticeDocumentMetadata)[] = [
      'name', 'description', 'category', 'sendWithInvite', 'requiredInInvite', 'allowManualSend',
      'requiresResponse', 'sendWithConsentRequest', 'expiryDays', 'active',
      // §26. Adding a field to the interface is NOT enough — this list is the body, deliberately,
      // and a field missing from it is silently dropped. That is the guard working: the same one
      // that stops a form control becoming a request field by accident.
      'signOffUserIds', 'validationUserIds',
    ];
    for (const key of keys) {
      if (metadata[key] !== undefined) body[key] = metadata[key];
    }
    return body;
  }

  private authHeaders(): { [header: string]: string } {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }
}
